import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import {connect} from 'react-redux';
import {reduxForm, Field, formValueSelector} from 'redux-form';

import {required, validDate} from 'utils/validators';
import {getCurrentMonthFirstDay} from 'utils/datetime';
import {formatDateForApi} from 'utils/datetime';

import FormField from 'components/FormField';
import Label from 'components/Label';
import BookingInformation from 'components/BookingInformation';
import {
  TextField,
  MoneyField,
  DateField,
  SearchField,
  SelectField
} from 'components/Fields';

import {
  MODALS,
  ACCOUNT_TYPES,
  FETCH_SCHEDULE_DELAY,
  createOverpayment,
  fetchXeroAccounts,
  SCHEDULE_TYPES,
  SCHEDULE_TYPES_HUMAN_VALUES,
  fetchCanCreateFinanceItemForPastMonths
} from 'ducks/tenancy';
import {selectModal, storeDataInModal} from 'ducks/modals';
import {delayedAction, composeActions} from 'ducks/common';

import './styles.css';

const SCHEDULE_TYPE_OPTIONS = Object.keys(SCHEDULE_TYPES).map(key => ({
  label: SCHEDULE_TYPES_HUMAN_VALUES[key],
  value: SCHEDULE_TYPES[key]
}));

const formSubmit = (values, dispatch, props) => {
  const {tenancyId} = props;
  const scheduleType = props.scheduleType || props.selectedScheduleType;

  const data = {
    xeroAccount: _.get(values, 'paidTo.id'),
    amount: values.amount,
    paymentDate: formatDateForApi(values.paymentDate),
    description: values.description,
    tenancyId,
    scheduleType
  };

  const extraActions = props.getExtraActions({scheduleType});
  const delayedExtraActions = props.delayExtraActions({
    tenancyId: props.tenancyId,
    scheduleType
  });

  if (!_.isEmpty(delayedExtraActions)) {
    const delayedFetchSchedule = () =>
      delayedAction({
        delay: FETCH_SCHEDULE_DELAY,
        action: () => composeActions(delayedExtraActions)
      });
    extraActions.push(delayedFetchSchedule);
  }

  dispatch(createOverpayment(data, extraActions));
};

class OverpaymentCreateForm extends React.Component<any> {
  componentDidMount() {
    const {locationRef} = this.props;

    const fetchXeroAccountsExtraAction = this.props.storeDataExtraActions(
      data => ({
        xeroAccounts: data
      })
    );
    this.props.fetchXeroAccounts(
      {
        accountType: ACCOUNT_TYPES.BANK,
        location: locationRef
      },
      fetchXeroAccountsExtraAction
    );
    const fetchCanCreateFinanceItemForPastMonthsExtraAction = this.props.storeDataExtraActions(
      data => ({
        canCreateOverpaymentForPastMonths: _.get(
          data,
          'canCreateFinanceItemsForPastMonths'
        )
      })
    );

    this.props.fetchCanCreateFinanceItemForPastMonths(
      {locationRef: this.props.locationRef},
      fetchCanCreateFinanceItemForPastMonthsExtraAction
    );
  }

  render() {
    const xeroAccounts = this.props.xeroAccounts || [];
    const {
      openFromBookingSet,
      booking,
      canCreateOverpaymentForPastMonths
    } = this.props;

    let datepickerProps: any = undefined;
    const currentMonthFirstDay: any = getCurrentMonthFirstDay();

    if (!canCreateOverpaymentForPastMonths) {
      datepickerProps = {
        disabledDays: {before: new Date(currentMonthFirstDay)}
      };
    }

    const DatePickerWithPermissionsOverlay = ({
      children,
      onBlur,
      onFocus,
      tabIndex,
      month // month here is the first day of the current selected month
    }: any) => {
      const shouldShowWarning =
        !canCreateOverpaymentForPastMonths &&
        moment(month) < currentMonthFirstDay;

      return (
        <div
          className="DayPickerInput-OverlayWrapper"
          onBlur={onBlur}
          onFocus={onFocus}
          tabIndex={tabIndex}>
          <div className="DayPickerInput-Overlay">
            {children}
            {shouldShowWarning && (
              <div className="colab-centered colab-bold ">
                You do not have the permission!
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <form className="OverpaymentCreateForm">
        {openFromBookingSet && <BookingInformation booking={booking} />}
        <FormField>
          <Label>Tenant</Label>
          <Field
            name="tenant"
            component={TextField}
            format={tenant => _.get(tenant, 'fullName')}
            disabled={true}
          />
        </FormField>
        {openFromBookingSet && (
          <FormField>
            <Label>Category</Label>
            <Field
              name="scheduleType"
              validate={[required]}
              component={SelectField}
              options={SCHEDULE_TYPE_OPTIONS}
            />
          </FormField>
        )}
        <FormField>
          <Label>Amount</Label>
          <Field name="amount" component={MoneyField} validate={[required]} />
        </FormField>
        <FormField>
          <Label>Overpayment date</Label>
          <Field
            name="paymentDate"
            component={DateField}
            validate={[validDate]}
            dayPickerProps={datepickerProps}
            overlayComponent={DatePickerWithPermissionsOverlay}
          />
        </FormField>
        <FormField>
          <Label>Paid to</Label>
          <Field
            name="paidTo"
            component={SearchField}
            validate={[required]}
            options={xeroAccounts.map(acc => ({
              value: `${acc.id} - ${acc.label}`,
              label: acc.label,
              id: acc.id
            }))}
          />
        </FormField>
        <FormField>
          <Label>Reference</Label>
          <Field
            name="description"
            component={TextField}
            validate={[required]}
          />
        </FormField>
      </form>
    );
  }
}

const formSelector = formValueSelector('overpaymentCreate');

const mapStateToProps = (state, props) => {
  const {data: {xeroAccounts}} = selectModal(state, MODALS.OVERPAYMENT_CREATE);

  return {
    xeroAccounts,
    initialValues: {
      tenant: props.primaryTenant
    },
    selectedScheduleType: _.get(formSelector(state, 'scheduleType'), 'value')
  };
};

const mapDispatchToProps = {
  fetchXeroAccounts,
  storeDataInModal,
  fetchCanCreateFinanceItemForPastMonths
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'overpaymentCreate',
    onSubmit: formSubmit
  })(OverpaymentCreateForm)
);
